import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileDown } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OfferteAccept() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get(`/public/offerte/${token}`);
                setDoc(data);
                if (data.status === "akkoord") setAccepted(true);
            } catch {
                setError(true);
            }
        })();
    }, [token]);

    const accept = async () => {
        setBusy(true);
        try {
            await platformClient.post(`/public/offerte/${token}/accept`);
            setAccepted(true);
        } catch {} finally {
            setBusy(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const res = await platformClient.get(`/public/invoice-pdf/${token}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${doc.number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {}
    };

    return (
        <div data-testid="offerte-accept-page" className="min-h-screen bg-ink px-6 py-12 text-bone">
            <div className="mx-auto max-w-2xl">
                <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="start" />

                {error ? (
                    <div className="mt-16 border border-white/10 bg-coal p-10 text-center">
                        <p data-testid="offerte-invalid" className="font-display text-2xl">Link ongeldig of verlopen</p>
                        <p className="mt-3 text-sm text-ash">Vraag de afzender om een nieuwe link.</p>
                    </div>
                ) : !doc ? (
                    <p className="mt-16 text-xs uppercase tracking-[0.3em] text-ash">Laden…</p>
                ) : (
                    <div className="mt-10 border border-white/10 bg-coal">
                        <div className="border-b border-white/10 p-8">
                            {doc.sender?.logo_url ? (
                                <img
                                    data-testid="offerte-tenant-logo"
                                    src={`${process.env.REACT_APP_BACKEND_URL}${doc.sender.logo_url}`}
                                    alt={doc.sender?.name}
                                    className="mb-5 max-h-12 max-w-48 object-contain"
                                />
                            ) : null}
                            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Offerte {doc.number}</p>
                            <h1 className="mt-3 font-display text-3xl tracking-tight">{doc.sender?.name}</h1>
                            <p className="mt-2 text-sm text-ash">Voor {doc.company} · {doc.issue_date}</p>
                        </div>
                        <table className="w-full px-8 text-sm">
                            <tbody className="p-8">
                                {(doc.items || []).map((item, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                        <td className="py-3 pl-8 text-bone">{item.description}</td>
                                        <td className="py-3 text-right text-ash">{item.quantity} ×</td>
                                        <td className="py-3 text-right text-ash">{euro(item.unit_price)}</td>
                                        <td className="py-3 pr-8 text-right text-bone">{euro(item.quantity * item.unit_price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex justify-end p-8">
                            <div className="w-64 text-sm">
                                <p className="flex justify-between text-ash"><span>Subtotaal</span><span>{euro(doc.subtotal)}</span></p>
                                <p className="mt-1 flex justify-between text-ash"><span>BTW {doc.vat_rate}%</span><span>{euro(doc.vat_amount)}</span></p>
                                <p className="mt-3 flex justify-between border-t border-gold/30 pt-3">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Totaal</span>
                                    <span data-testid="offerte-total" className="font-display text-2xl text-gold">{euro(doc.total)}</span>
                                </p>
                            </div>
                        </div>
                        {doc.notes ? <p className="border-t border-white/10 px-8 py-5 text-xs leading-relaxed text-ash">{doc.notes}</p> : null}
                        <div className="flex flex-wrap gap-4 border-t border-white/10 p-8">
                            {accepted ? (
                                <p data-testid="offerte-accepted" className="flex items-center gap-3 border border-gold/40 bg-gold/10 px-6 py-4 text-sm text-gold">
                                    <CheckCircle2 className="h-5 w-5" /> Akkoord — bedankt! De opdracht is doorgezet naar productie.
                                </p>
                            ) : (
                                <button data-testid="offerte-accept-button" onClick={accept} disabled={busy}
                                    className="bg-gold px-10 py-4 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-bone disabled:opacity-60">
                                    {busy ? "Bezig…" : "Akkoord geven"}
                                </button>
                            )}
                            <button data-testid="offerte-download-pdf" onClick={downloadPdf}
                                className="flex items-center gap-2 border border-white/15 px-6 py-4 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                <FileDown className="h-4 w-4" /> PDF downloaden
                            </button>
                        </div>
                    </div>
                )}
                <p className="mt-8 text-center text-[10px] uppercase tracking-[0.3em] text-ash/50">
                    Verzonden via Clarke Business Solutions
                </p>
            </div>
        </div>
    );
}
